import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../ComingSoon/_shared/AppIcon';
import { LeaveRequestView, LeaveRequestStatus } from '../../../../models/attendance';

interface LeaveHistoryListProps {
  history: LeaveRequestView[];
  onViewDetails: (record: LeaveRequestView) => void;
  onCancel?: (record: LeaveRequestView) => void;
}

interface StatusColors {
  bg: string;
  text: string;
  dot: string;
}

function getStatusColors(status: LeaveRequestStatus): StatusColors {
  const statusMap: Record<string, StatusColors> = {
    [LeaveRequestStatus.Approved]: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    [LeaveRequestStatus.Rejected]: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
    [LeaveRequestStatus.Pending]: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
    [LeaveRequestStatus.Cancelled]: { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' },
    [LeaveRequestStatus.CancellationPending]: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  };
  return statusMap[status] || statusMap[LeaveRequestStatus.Pending];
}

const LeaveHistoryList: React.FC<LeaveHistoryListProps> = ({ history, onViewDetails, onCancel }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [sortBy, setSortBy] = useState<string>('lastUpdate');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const sortedAndFilteredHistory = useMemo(() => {
    let filtered = history;
    if (filterStatus !== 'all') {
      filtered = history.filter(item => item.status === filterStatus);
    }

    return [...filtered].sort((a, b) => {
      const aVal = sortBy === 'startDate' ? new Date(a.startDate).getTime() : new Date(a.lastUpdate).getTime();
      const bVal = sortBy === 'startDate' ? new Date(b.startDate).getTime() : new Date(b.lastUpdate).getTime();
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [history, sortBy, sortOrder, filterStatus]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (!history || history.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t('No Leave History')}</h3>
        <p className="text-muted-foreground">{t('noHistoryMessage')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Sorting */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <div>
            <label className="text-sm font-medium text-muted-foreground ltr:mr-2 rtl:ml-2">{t('Status')}:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="leave-status-filter"
              className="px-3 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('All Statuses')}</option>
              <option value={LeaveRequestStatus.Pending}>{t('Pending')}</option>
              <option value={LeaveRequestStatus.Approved}>{t('Approved')}</option>
              <option value={LeaveRequestStatus.Rejected}>{t('Rejected')}</option>
              <option value={LeaveRequestStatus.Cancelled}>{t('Cancelled')}</option>
              <option value={LeaveRequestStatus.CancellationPending}>{t('Cancellation Pending')}</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <span className="text-sm text-muted-foreground">{t('Sort by')}:</span>
          <button
            onClick={() => handleSort('lastUpdate')}
            data-testid="leave-history-sort-applied"
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'lastUpdate' ? 'bg-emerald-100 text-emerald-700' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {t('Applied Date')}
            {sortBy === 'lastUpdate' && (
              <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} className="inline ltr:ml-1 rtl:mr-1" />
            )}
          </button>
          <button
            onClick={() => handleSort('startDate')}
            data-testid="leave-history-sort-start"
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'startDate' ? 'bg-emerald-100 text-emerald-700' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {t('Start Date')}
            {sortBy === 'startDate' && (
              <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} className="inline ltr:ml-1 rtl:mr-1" />
            )}
          </button>
        </div>
      </div>

      {/* History List */}
      <div className="space-y-3">
        {sortedAndFilteredHistory.map((record) => {
          const statusColors = getStatusColors(record.status);
          const isExpanded = expandedItems.has(record.id);

          return (
            <div
              key={record.id}
              data-testid="leave-history-item"
              className="border border-border rounded-lg bg-card hover:shadow-md transition-all duration-200"
            >
              {/* Main Row */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 rtl:space-x-reverse flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center">
                        <Icon name="Calendar" size={20} className="text-blue-600" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 rtl:space-x-reverse mb-1">
                        <h3 className="text-base font-medium text-foreground">
                          {record.leaveType}
                        </h3>
                        <div className={`flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}>
                          <div className={`w-2 h-2 rounded-full ${statusColors.dot}`} />
                          <span>{t(record.status)}</span>
                        </div>
                        {record.fromHalfDay && (
                          <div className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            {t('Half day')}
                          </div>
                        )}
                        {record.isOnBehalf && record.submittedByName && (
                          <div
                            data-testid="on-behalf-badge"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
                          >
                            <Icon name="UserPlus" size={10} />
                            {t('onBehalfBadge', { name: record.submittedByName })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-muted-foreground">
                        <span className="flex items-center">
                          <Icon name="Calendar" size={14} className="ltr:mr-1 rtl:ml-1" />
                          {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Clock" size={14} className="ltr:mr-1 rtl:ml-1" />
                          {record.numberOfDays} {record.numberOfDays === 1 ? t('day') : t('days')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    {(record.status === LeaveRequestStatus.Pending || record.status === LeaveRequestStatus.Approved) && onCancel && (
                      <button
                        onClick={() => onCancel(record)}
                        data-testid="cancel-leave-btn"
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title={record.status === LeaveRequestStatus.Approved ? t('Request Cancellation') : t('Cancel Request')}
                      >
                        <Icon name="XCircle" size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => onViewDetails(record)}
                      data-testid="leave-history-view-details"
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                      title={t('View Details')}
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => toggleExpanded(record.id)}
                      data-testid="leave-history-expand"
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                      title={isExpanded ? t('Collapse') : t('Expand')}
                    >
                      <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-border bg-muted/20">
                  <div className="pt-4 space-y-4">
                    {/* Reason */}
                    {record.comments && (
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">{t('Reason')}</h4>
                        <p className="text-sm text-muted-foreground">{record.comments}</p>
                      </div>
                    )}

                    {/* Timeline */}
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-3">{t('Request Timeline')}</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-foreground">{t('Application Submitted')}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(record.lastUpdate).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('submittedDescription')}</p>
                          </div>
                        </div>

                        {record.status !== LeaveRequestStatus.Pending && record.status !== LeaveRequestStatus.CancellationPending && (
                          record.reviewers && record.reviewers.length > 0 ? (
                            record.reviewers.map((reviewer, idx) => (
                              <div key={idx} className="flex items-center space-x-3 rtl:space-x-reverse">
                                <div className={`w-2 h-2 rounded-full ${
                                  reviewer.status === 'Approved' ? 'bg-green-500' :
                                  reviewer.status === 'Rejected' ? 'bg-red-500' : 'bg-gray-500'
                                }`} />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-foreground">
                                      {reviewer.status === 'Approved' ? t('Approved by') : t('Rejected by')} {reviewer.reviewerName}
                                    </span>
                                    {reviewer.actionDate && (
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(reviewer.actionDate).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                  {reviewer.comments && (
                                    <p className="text-xs text-muted-foreground">{reviewer.comments}</p>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : record.managerName ? (
                            <div className="flex items-center space-x-3 rtl:space-x-reverse">
                              <div className={`w-2 h-2 rounded-full ${statusColors.dot}`} />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-foreground">
                                    {record.status === LeaveRequestStatus.Approved ? t('Approved by') :
                                     record.status === LeaveRequestStatus.Cancelled ? t('Cancelled') :
                                     t('Rejected by')} {record.status !== LeaveRequestStatus.Cancelled ? record.managerName : ''}
                                  </span>
                                </div>
                                {record.managerComments && (
                                  <p className="text-xs text-muted-foreground">{record.managerComments}</p>
                                )}
                              </div>
                            </div>
                          ) : null
                        )}

                        {record.status === LeaveRequestStatus.Pending && (
                          <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-foreground">{t('Pending Approval')}</span>
                              <p className="text-xs text-muted-foreground">{t('pendingMessage')}</p>
                            </div>
                          </div>
                        )}

                        {record.status === LeaveRequestStatus.CancellationPending && (
                          <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-foreground">{t('Cancellation Pending Approval')}</span>
                              <p className="text-xs text-muted-foreground">{t('cancellationPendingMessage')}</p>
                            </div>
                          </div>
                        )}

                        {record.status === LeaveRequestStatus.Cancelled && (
                          <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="w-2 h-2 bg-gray-500 rounded-full" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-foreground">{t('Request Cancelled')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Attachments */}
                    {record.attachmentCount > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-2">{t('Attachments')}</h4>
                        <p className="text-sm text-muted-foreground">
                          {record.attachmentCount} {t('Attachments')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground text-center">
          {t('Showing')} {sortedAndFilteredHistory.length} {t('of')} {history.length} {t('leave requests')}
        </p>
      </div>
    </div>
  );
};

export default LeaveHistoryList;
